import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sfx } from "../lib/sound";
import { playCoachCountdown } from "../lib/coach";

/**
 * Tabata-style 3-2-1-GO countdown shown before every game starts.
 * Snappy synth beeps drive the cadence — no voice clip (which was
 * 4-7s long and felt out of sync with the visuals). The coach voice is
 * reserved for impactful moments (wins, combos, time-up).
 *
 * Total duration: ~2.8s (3×700ms + 700ms for GO).
 */
export default function CountdownOverlay({ onComplete }) {
  const [step, setStep] = useState(3);

  // Each step plays a synth beep on entry, plus (in Polish) a spoken number
  // clip synced with the on-screen 3/2/1/GO so the count is clearly heard.
  useEffect(() => {
    if (step === "GO") sfx.match();
    else if (step !== null) sfx.tap();
    if (step !== null) playCoachCountdown(step);
  }, [step]);

  useEffect(() => {
    if (step === null) return;
    const t = setTimeout(() => {
      if (step === 3) setStep(2);
      else if (step === 2) setStep(1);
      else if (step === 1) setStep("GO");
      else if (step === "GO") {
        setStep(null);
        onComplete && onComplete();
      }
    }, 700);
    return () => clearTimeout(t);
  }, [step, onComplete]);

  if (step === null) return null;

  const isGo = step === "GO";
  return (
    <motion.div
      data-testid="countdown-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-auto"
      style={{
        background: "radial-gradient(ellipse at center, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.92) 100%)",
        backdropFilter: "blur(6px)",
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={String(step)}
          initial={{ scale: 0.4, opacity: 0, rotate: -8 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          exit={{ scale: 1.6, opacity: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 18 }}
          className="select-none"
          style={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 700,
            fontSize: isGo ? "clamp(120px, 28vw, 220px)" : "clamp(140px, 32vw, 240px)",
            lineHeight: 1,
            color: isGo ? "#CDE079" : "#F7F5F0",
            letterSpacing: isGo ? "0.04em" : "-0.04em",
            textShadow: isGo
              ? "0 0 32px rgba(205,224,121,0.6), 0 8px 32px rgba(0,0,0,0.7)"
              : "0 0 24px rgba(232,163,101,0.5), 0 8px 24px rgba(0,0,0,0.6)",
          }}
        >
          {step}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
